import { useEffect, useMemo, useState } from 'react';
import type { FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { fieldWorkApi } from '../../api/field-work-api';
import { inventoryApi } from '../../api/inventory-api-instance';
import { MultiSelectFilter } from '../../components/MultiSelectFilter';
import { SearchableSelect } from '../../components/SearchableSelect';
import {
  fieldWorkLogisticsModes,
  fieldWorkPriorities,
  fieldWorkSources,
  logisticsModeLabels,
  priorityLabels,
  sourceLabels,
  type FieldWorkLogisticsMode,
  type FieldWorkPriority,
  type FieldWorkRequest,
  type FieldWorkSource,
} from '../../domain/fieldWorkRequest';
import {
  DEFAULT_TEAM,
  fieldOpsTeams,
  teamForCountry,
  teamLabels,
  type FieldOpsTeam,
} from '../../domain/fieldOpsTeam';
import { resolveFieldOpsPerson } from '../../domain/fieldOpsPeople';
import { fieldOpsPeopleApi } from '../../api/field-ops-people-api';
import { useAuth } from '../../auth/AuthContext';

interface NewFieldWorkRequestDrawerProps {
  onClose: () => void;
  onCreated: (request: FieldWorkRequest) => void;
  defaultTeam?: FieldOpsTeam | undefined;
}

interface Draft {
  title: string;
  description: string;
  source: FieldWorkSource | '';
  customerId: string;
  siteId: string;
  relatedItemIds: string[];
  team: FieldOpsTeam;
  teamTouched: boolean;
  sourceTouched: boolean;
  priority: FieldWorkPriority | '';
  dueDate: string;
  githubIssueUrl: string;
  logisticsMode: FieldWorkLogisticsMode;
  logisticsNotes: string;
}

function emptyDraft(defaultTeam: FieldOpsTeam | undefined): Draft {
  // Source and (when no team tab forced one) Team are prefilled once the
  // Field Ops directory query resolves -- see the effect below.
  return {
    title: '',
    description: '',
    source: '',
    customerId: '',
    siteId: '',
    relatedItemIds: [],
    team: defaultTeam ?? DEFAULT_TEAM,
    teamTouched: false,
    sourceTouched: false,
    priority: '',
    dueDate: '',
    githubIssueUrl: '',
    logisticsMode: 'NONE',
    logisticsNotes: '',
  };
}

export function NewFieldWorkRequestDrawer({
  onClose,
  onCreated,
  defaultTeam,
}: NewFieldWorkRequestDrawerProps) {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const [draft, setDraft] = useState<Draft>(() => emptyDraft(defaultTeam));
  const [formError, setFormError] = useState<string | null>(null);
  const [siteCode, setSiteCode] = useState('');
  const [siteCodeStatus, setSiteCodeStatus] = useState<string | null>(null);

  // Resolve a site by its short name or official code -> customer + site,
  // so a requester who only knows "BAA" doesn't have to guess the customer
  // first. Uses the unscoped ListSites (GET /v1/sites), cached per session.
  const siteCodeLookup = useMutation({
    mutationFn: async (term: string) => {
      const all = await queryClient.fetchQuery({
        queryKey: ['sites', 'all'],
        queryFn: () => inventoryApi.listSites(),
        staleTime: 5 * 60 * 1000,
      });
      const needle = term.trim().toLowerCase();
      const fields = (site: (typeof all)[number]) =>
        [site.officialSiteId ?? '', site.name, site.displayName ?? ''].map((value) =>
          value.toLowerCase(),
        );
      return (
        all.find((site) => fields(site).some((value) => value === needle)) ??
        all.find((site) => fields(site).some((value) => value.includes(needle))) ??
        null
      );
    },
    onSuccess: (site) => {
      if (!site) {
        setSiteCodeStatus(`No active site matches "${siteCode.trim()}".`);
        return;
      }
      setDraft((current) => ({ ...current, customerId: site.customerId, siteId: site.id }));
      setSiteCodeStatus(
        `Matched ${site.name}${site.officialSiteId ? ` (${site.officialSiteId})` : ''}.`,
      );
    },
    onError: () => setSiteCodeStatus('Site lookup failed — pick the customer and site below.'),
  });

  // The Field Ops directory (leatherback Users.ListFieldOpsPeople), used
  // only to prefill Source/Team from whoever is signed in.
  const fieldOpsPeople = useQuery({
    queryKey: ['field-ops-people'],
    queryFn: () => fieldOpsPeopleApi.listFieldOpsPeople(),
    staleTime: 30 * 60 * 1000,
  });

  const customers = useQuery({
    queryKey: ['customers'],
    queryFn: () => inventoryApi.listCustomers(),
    staleTime: 5 * 60 * 1000,
  });
  const sites = useQuery({
    queryKey: ['sites', draft.customerId],
    queryFn: () => inventoryApi.listSites(draft.customerId),
    enabled: Boolean(draft.customerId),
  });
  // Scope to the chosen customer/site when there is one (a short, relevant
  // list); otherwise pull a wide pool so a serial like "G8CS…" / "VVWS…"
  // can be found across the fleet, not just the first page of IDs.
  const items = useQuery({
    queryKey: ['items', 'field-work-picker', draft.customerId, draft.siteId],
    queryFn: () =>
      inventoryApi.listItems({
        pageSize: 5000,
        ...(draft.customerId ? { customerId: draft.customerId } : {}),
        ...(draft.siteId ? { siteId: draft.siteId } : {}),
      }),
    staleTime: 5 * 60 * 1000,
  });

  const selectedSite = useMemo(
    () => (sites.data ?? []).find((site) => site.id === draft.siteId),
    [sites.data, draft.siteId],
  );

  // Prefill Source (and Team, when nothing has forced one yet) from the
  // signed-in person's Field Ops directory entry, once it loads.
  useEffect(() => {
    const people = fieldOpsPeople.data;
    if (!people) return;
    const me = resolveFieldOpsPerson(people, user?.email, user?.name);
    if (!me) return;
    setDraft((current) => {
      let next = current;
      if (!current.sourceTouched && current.source === '' && me.source) {
        next = { ...next, source: me.source };
      }
      if (!current.teamTouched && !defaultTeam && !current.siteId && me.team) {
        next = { ...next, team: me.team };
      }
      return next;
    });
  }, [fieldOpsPeople.data, user?.email, user?.name, defaultTeam]);

  // Suggest the team from the site's country unless the user has picked one.
  useEffect(() => {
    if (draft.teamTouched || !selectedSite) return;
    const suggested = teamForCountry(selectedSite.labels?.country);
    setDraft((current) =>
      current.team === suggested ? current : { ...current, team: suggested },
    );
  }, [selectedSite, draft.teamTouched]);

  const createMutation = useMutation({
    mutationFn: () =>
      fieldWorkApi.createFieldWork({
        title: draft.title.trim(),
        description: draft.description.trim() || undefined,
        source: draft.source || undefined,
        customerId: draft.customerId || undefined,
        siteId: draft.siteId || undefined,
        relatedItemIds: draft.relatedItemIds.length ? draft.relatedItemIds : undefined,
        githubIssueUrl: draft.githubIssueUrl.trim() || undefined,
        team: draft.team,
        priority: draft.priority || undefined,
        dueDate: draft.dueDate
          ? new Date(`${draft.dueDate}T00:00:00.000Z`).toISOString()
          : undefined,
        logistics:
          draft.logisticsMode === 'NONE' && !draft.logisticsNotes.trim()
            ? undefined
            : {
                mode: draft.logisticsMode,
                notes: draft.logisticsNotes.trim() || undefined,
              },
      }),
    onSuccess: async (created) => {
      await queryClient.invalidateQueries({ queryKey: ['field-work'] });
      onCreated(created);
    },
    onError: (error: unknown) => {
      setFormError(error instanceof Error ? error.message : 'The request could not be created.');
    },
  });

  function update<K extends keyof Draft>(key: K, value: Draft[K]): void {
    setDraft((current) => ({ ...current, [key]: value }));
  }

  function handleSubmit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    setFormError(null);
    if (!draft.title.trim()) {
      setFormError('A short title is required.');
      return;
    }
    createMutation.mutate();
  }

  const itemOptions = useMemo(
    () => (items.data?.data ?? []).map((item) => ({ value: item.id, label: item.id })),
    [items.data],
  );

  return (
    <div className="drawer-overlay" role="presentation" onClick={onClose}>
      <form
        className="drawer"
        aria-label="New field work request"
        onClick={(event) => event.stopPropagation()}
        onSubmit={handleSubmit}
      >
        <div className="drawer-heading">
          <h2>New request</h2>
          <button
            type="button"
            className="icon-button"
            onClick={onClose}
            aria-label="Close new request drawer"
          >
            ×
          </button>
        </div>

        {formError ? (
          <p role="alert" className="form-error">
            {formError}
          </p>
        ) : null}

        <label>
          Title
          <input
            value={draft.title}
            onChange={(event) => update('title', event.target.value)}
            placeholder="e.g. Replace broken system G8CS23285140 at BIB"
            required
          />
        </label>
        <label>
          Description
          <textarea
            value={draft.description}
            onChange={(event) => update('description', event.target.value)}
            rows={3}
          />
        </label>

        <label>
          Source
          <SearchableSelect
            ariaLabel="Source"
            value={draft.source}
            onChange={(value) =>
              setDraft((current) => ({
                ...current,
                source: value as FieldWorkSource | '',
                sourceTouched: true,
              }))
            }
            options={[
              { value: '', label: 'Unspecified' },
              ...fieldWorkSources.map((source) => ({ value: source, label: sourceLabels[source] })),
            ]}
            placeholder="Unspecified"
          />
        </label>

        <label>
          Find a site by name or code
          <span className="inline-input-button">
            <input
              value={siteCode}
              onChange={(event) => setSiteCode(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' && siteCode.trim()) {
                  event.preventDefault();
                  setSiteCodeStatus(null);
                  siteCodeLookup.mutate(siteCode);
                }
              }}
              placeholder="e.g. BAA — fills in the customer + site below"
            />
            <button
              type="button"
              disabled={!siteCode.trim() || siteCodeLookup.isPending}
              onClick={() => {
                setSiteCodeStatus(null);
                siteCodeLookup.mutate(siteCode);
              }}
            >
              {siteCodeLookup.isPending ? 'Finding…' : 'Find'}
            </button>
          </span>
        </label>
        {siteCodeStatus ? (
          <p className="drawer-version-note">{siteCodeStatus}</p>
        ) : null}

        <div className="form-row">
          <label>
            Customer
            <SearchableSelect
              ariaLabel="Customer"
              value={draft.customerId}
              onChange={(value) =>
                setDraft((current) => ({ ...current, customerId: value, siteId: '' }))
              }
              options={[
                { value: '', label: 'No customer' },
                ...(customers.data ?? [])
                  .map((customer) => ({ value: customer.id, label: customer.name }))
                  .sort((a, b) => a.label.localeCompare(b.label)),
              ]}
              placeholder="No customer"
            />
          </label>
          <label>
            Site
            <SearchableSelect
              ariaLabel="Site"
              value={draft.siteId}
              onChange={(value) => update('siteId', value)}
              options={[
                { value: '', label: 'No site' },
                ...(sites.data ?? [])
                  .map((site) => ({
                    value: site.id,
                    // Site codes are unique -- surface the official code so
                    // the site can be found by code, not just display name.
                    label: site.officialSiteId
                      ? `${site.name} · ${site.officialSiteId}`
                      : site.name,
                  }))
                  .sort((a, b) => a.label.localeCompare(b.label)),
              ]}
              placeholder={draft.customerId ? 'No site' : 'Pick a customer first'}
            />
          </label>
        </div>

        <label>
          Related assets
          <MultiSelectFilter
            ariaLabel="Related assets"
            values={draft.relatedItemIds}
            onChange={(values) => update('relatedItemIds', values)}
            options={itemOptions}
            placeholder="Search item IDs"
            requireSearchToBrowse
          />
        </label>

        <div className="form-row">
          <label>
            Field Ops team
            <SearchableSelect
              ariaLabel="Field Ops team"
              value={draft.team}
              onChange={(value) =>
                setDraft((current) => ({
                  ...current,
                  team: value as FieldOpsTeam,
                  teamTouched: true,
                }))
              }
              options={fieldOpsTeams.map((team) => ({ value: team, label: teamLabels[team] }))}
              placeholder="Team"
            />
          </label>
          <label>
            Priority
            <SearchableSelect
              ariaLabel="Priority"
              value={draft.priority}
              onChange={(value) => update('priority', value as FieldWorkPriority | '')}
              options={[
                { value: '', label: 'Unset' },
                ...fieldWorkPriorities.map((priority) => ({
                  value: priority,
                  label: priorityLabels[priority],
                })),
              ]}
              placeholder="Unset"
            />
          </label>
        </div>
        {!draft.teamTouched && selectedSite ? (
          <p className="drawer-version-note">
            Team suggested from {selectedSite.name}
            {selectedSite.labels?.country ? ` (${selectedSite.labels.country})` : ''} — change it if
            another team should handle this.
          </p>
        ) : null}

        <div className="form-row">
          <label>
            Due date
            <input
              type="date"
              value={draft.dueDate}
              onChange={(event) => update('dueDate', event.target.value)}
            />
          </label>
          <label>
            GitHub issue
            <input
              type="url"
              value={draft.githubIssueUrl}
              onChange={(event) => update('githubIssueUrl', event.target.value)}
              placeholder="https://github.com/tidaltech/koi/issues/…"
            />
          </label>
        </div>

        <label>
          Logistics
          <SearchableSelect
            ariaLabel="Logistics"
            value={draft.logisticsMode}
            onChange={(value) => update('logisticsMode', value as FieldWorkLogisticsMode)}
            options={fieldWorkLogisticsModes.map((mode) => ({
              value: mode,
              label: logisticsModeLabels[mode],
            }))}
            placeholder="No shipping required"
          />
        </label>
        {draft.logisticsMode !== 'NONE' ? (
          <label>
            Logistics notes
            <input
              value={draft.logisticsNotes}
              onChange={(event) => update('logisticsNotes', event.target.value)}
            />
          </label>
        ) : null}

        <div className="drawer-actions">
          <button type="button" onClick={onClose} disabled={createMutation.isPending}>
            Cancel
          </button>
          <button type="submit" className="primary" disabled={createMutation.isPending}>
            {createMutation.isPending ? 'Creating…' : 'Create request'}
          </button>
        </div>
      </form>
    </div>
  );
}
